/**
 * Beybling Logo Arena
 * A tiny dependency-free 2D physics playground: spin tops (logos) fall, bounce,
 * collide, and can be dragged / flung. Tap a top to "let it rip".
 *
 * Each arena is an element with class `.beybling-arena` and a `data-logo` URL.
 */
(function () {
  const A = window.BeyblingArena || {};
  const REDUCED = A.REDUCED ?? window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = A.clamp ?? ((v, min, max) => Math.max(min, Math.min(max, v)));
  const rand = A.rand ?? ((min, max) => min + Math.random() * (max - min));
  const measureArenaViewport =
    A.measureArenaViewport ??
    ((arena) => {
      const rect = arena.getBoundingClientRect();
      return { width: Math.max(rect.width, 1), height: Math.max(rect.height, 1), rect };
    });
  const measureArenaBounds =
    A.measureArenaBounds ??
    ((arena) => {
      const size = measureArenaViewport(arena);
      return { ...size, ceiling: 0, floor: size.height };
    });
  const createArenaTiltGravity =
    A.createArenaTiltGravity ??
    (() => ({
      gx: 0,
      gy: 0.5,
      active: false,
      tick() {},
      startListening() {},
      stopListening() {},
      requestOnGesture() {},
    }));

  /** @type {Map<HTMLElement, () => void>} */
  const arenaCleanups = new Map();

  function destroyArena(arena) {
    if (!(arena instanceof HTMLElement)) return;

    const cleanup = arenaCleanups.get(arena);
    if (cleanup) {
      cleanup();
      arenaCleanups.delete(arena);
    }

    arena.querySelectorAll('.beybling-arena__logo').forEach((logo) => logo.remove());
    delete arena.dataset.beyblingArenaReady;
  }

  function initArena(arena) {
    if (arena.dataset.duel === 'true' || arena.classList.contains('beybling-arena--duel')) return;
    if (arena.dataset.beyblingArenaReady === 'true') return;

    const logoSrc = arena.dataset.logo;
    if (!logoSrc) return;

    const isFullPage = arena.dataset.fullpage === 'true' || arena.classList.contains('beybling-arena--fullpage');
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const requestedCount = clamp(parseInt(arena.dataset.count || '18', 10) || 18, 3, 250);
    const desktopScale = A.DESKTOP_BODY_SCALE ?? 2.4;
    const desktopMax = A.MAX_BODIES_FULLPAGE_DESKTOP ?? 40;
    const mobileFullMax = A.MAX_BODIES_FULLPAGE_MOBILE ?? 40;
    const boxedMax = A.MAX_BODIES_BOXED ?? 24;
    const count = isFullPage
      ? isDesktop
        ? clamp(Math.round(requestedCount * desktopScale), requestedCount, desktopMax)
        : clamp(requestedCount, 3, mobileFullMax)
      : clamp(requestedCount, 3, boxedMax);

    const GRAVITY = REDUCED ? 0 : 0.5;
    const AIR = 0.995;
    const WALL_RESTITUTION = 0.7;
    const BODY_RESTITUTION = 0.6;
    const FLOOR_FRICTION = 0.84;
    const isTouchDevice = 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;
    const useTiltGravity =
      isFullPage &&
      document.body.classList.contains('beybling-contact-arena') &&
      (window.matchMedia('(max-width: 749px)').matches || isTouchDevice) &&
      ('DeviceMotionEvent' in window || 'DeviceOrientationEvent' in window);
    /** @type {HTMLElement | null} */
    let hint = isFullPage ? arena.querySelector('.beybling-arena__hint') : null;
    const tiltGravity = useTiltGravity
      ? createArenaTiltGravity({
          strength: GRAVITY,
          onReady: function () {
            if (tiltUnlock instanceof HTMLButtonElement) tiltUnlock.hidden = true;
          },
        })
      : null;
    /** @type {HTMLButtonElement | null} */
    let tiltUnlock = null;

    if (hint instanceof HTMLElement) {
      hint.hidden = true;
    }

    /** @type {Array<any>} */
    const bodies = [];
    let width = arena.clientWidth || 1;
    let height = arena.clientHeight || 1;
    let ceiling = 0;
    let floor = height;
    let leftWall = 0;
    let rafId = null;
    let running = false;
    /** @type {ResizeObserver | null} */
    let resizeObserver = null;
    /** @type {IntersectionObserver | null} */
    let intersectionObserver = null;

    const measure = () => {
      const bounds = isFullPage ? measureArenaBounds(arena) : measureArenaViewport(arena);
      width = bounds.width;
      height = bounds.height;
      ceiling = isFullPage ? bounds.ceiling : 0;
      floor = isFullPage ? bounds.floor : height;
      leftWall = isFullPage && typeof bounds.leftWall === 'number' ? bounds.leftWall : 0;
    };
    measure();

    for (let i = 0; i < count; i++) {
      const r = isFullPage ? (isDesktop ? rand(51.6, 112) : rand(22, 48)) : rand(28, 56);
      const el = document.createElement('img');
      el.className = 'beybling-arena__logo beybling-gimmick-logo';
      el.src = logoSrc;
      el.alt = '';
      el.draggable = false;
      el.decoding = 'async';
      el.loading = isFullPage ? 'eager' : 'lazy';
      el.fetchPriority = isFullPage && i < 12 ? 'high' : 'auto';
      el.style.width = el.style.height = r * 2 + 'px';
      arena.appendChild(el);

      const minY = ceiling + r;
      const maxY = Math.max(minY, floor - r);
      bodies.push({
        el,
        r,
        x: rand(leftWall + r, Math.max(leftWall + r, leftWall + width - r)),
        y: rand(minY, maxY),
        vx: REDUCED ? 0 : rand(-4, 4),
        vy: REDUCED ? 0 : rand(-2, 4),
        a: rand(0, 360),
        va: REDUCED ? 0 : rand(-8, 8),
        dragging: false,
      });
    }

    if (REDUCED) {
      for (const b of bodies) {
        b.el.style.transform = `translate(${(b.x - b.r).toFixed(2)}px, ${(b.y - b.r).toFixed(2)}px) rotate(${b.a.toFixed(2)}deg)`;
      }

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(() => measure());
        resizeObserver.observe(arena);
      } else {
        window.addEventListener('resize', measure, { passive: true });
      }

      const cleanupReduced = () => {
        resizeObserver?.disconnect();
        window.removeEventListener('resize', measure);
      };
      arenaCleanups.set(arena, cleanupReduced);
      arena.dataset.beyblingArenaReady = 'true';
      return;
    }

    /* ---------------- pointer drag / fling ---------------- */
    let active = null;
    let pointerId = null;
    let movedDistance = 0;

    const localPoint = (event) => {
      const rect = arena.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const pick = (px, py) => {
      for (let i = bodies.length - 1; i >= 0; i--) {
        const b = bodies[i];
        if ((px - b.x) ** 2 + (py - b.y) ** 2 <= b.r * b.r) return b;
      }
      return null;
    };

    const onPointerDown = (event) => {
      if (!(event.target instanceof HTMLImageElement) || !event.target.classList.contains('beybling-arena__logo')) {
        return;
      }

      const body = bodies.find((b) => b.el === event.target);
      if (!body) return;

      active = body;
      pointerId = event.pointerId;
      movedDistance = 0;
      body.dragging = true;
      body.el.classList.add('is-grabbed');
      arena.appendChild(body.el);
      try {
        body.el.setPointerCapture(event.pointerId);
      } catch (e) {
        /* no-op */
      }
      event.preventDefault();
      event.stopPropagation();
    };

    const onPointerMove = (event) => {
      if (!active || event.pointerId !== pointerId) return;
      const { x, y } = localPoint(event);
      const dx = x - active.x;
      const dy = y - active.y;
      movedDistance += Math.abs(dx) + Math.abs(dy);
      active.vx = clamp(dx, -45, 45);
      active.vy = clamp(dy, -45, 45);
      active.x = x;
      active.y = y;
      active.va += dx * 0.6;
    };

    const release = (event) => {
      if (!active || (pointerId !== null && event.pointerId !== pointerId)) return;
      if (movedDistance < 6 && !REDUCED) {
        active.va += rand(35, 55) * (Math.random() < 0.5 ? -1 : 1);
        active.vy -= rand(6, 11);
        active.vx += rand(-4, 4);
      }
      active.dragging = false;
      active.el.classList.remove('is-grabbed');
      active = null;
      pointerId = null;
    };

    for (const body of bodies) {
      body.el.addEventListener('pointerdown', onPointerDown);
      body.el.addEventListener('pointermove', onPointerMove);
      body.el.addEventListener('pointerup', release);
      body.el.addEventListener('pointercancel', release);
    }

    /* legacy boxed arena: tap empty space still works */
    if (!isFullPage) {
      arena.addEventListener('pointerdown', (event) => {
        if (event.target !== arena) return;
        const { x, y } = localPoint(event);
        const body = pick(x, y);
        if (!body) return;
        active = body;
        pointerId = event.pointerId;
        movedDistance = 0;
        body.dragging = true;
        body.el.classList.add('is-grabbed');
        arena.appendChild(body.el);
        event.preventDefault();
      });
    }

    /* ---------------- collisions ---------------- */
    const collide = (a, b) => {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.hypot(dx, dy) || 0.0001;
      const minDist = a.r + b.r;
      if (dist >= minDist) return;

      const nx = dx / dist;
      const ny = dy / dist;
      const overlap = minDist - dist;

      const aShare = a.dragging ? 0 : b.dragging ? 1 : 0.5;
      const bShare = b.dragging ? 0 : a.dragging ? 1 : 0.5;
      a.x -= nx * overlap * aShare;
      a.y -= ny * overlap * aShare;
      b.x += nx * overlap * bShare;
      b.y += ny * overlap * bShare;

      const rvx = b.vx - a.vx;
      const rvy = b.vy - a.vy;
      const velAlongNormal = rvx * nx + rvy * ny;
      if (velAlongNormal > 0) return;

      const impulse = (-(1 + BODY_RESTITUTION) * velAlongNormal) / 2;
      const ix = impulse * nx;
      const iy = impulse * ny;
      if (!a.dragging) {
        a.vx -= ix;
        a.vy -= iy;
      }
      if (!b.dragging) {
        b.vx += ix;
        b.vy += iy;
      }

      const tangential = -rvx * ny + rvy * nx;
      a.va += tangential * 0.06;
      b.va -= tangential * 0.06;
    };

    /* ---------------- main loop ---------------- */
    const step = () => {
      if (!running) return;

      measure();
      if (tiltGravity) tiltGravity.tick();

      const gravityX = tiltGravity ? tiltGravity.gx : 0;
      const gravityY = tiltGravity ? tiltGravity.gy : GRAVITY;

      for (const b of bodies) {
        if (b.dragging) {
          b.va *= 0.92;
          if (isFullPage && b.y < ceiling + b.r) {
            b.y = ceiling + b.r;
          } else if (isFullPage && b.y > floor - b.r) {
            b.y = floor - b.r;
          }
        } else {
          b.vx += gravityX;
          b.vy += gravityY;
          b.vx *= AIR;
          b.vy *= AIR;
          b.x += b.vx;
          b.y += b.vy;

          if (b.x < leftWall + b.r) {
            b.x = leftWall + b.r;
            b.vx = Math.abs(b.vx) * WALL_RESTITUTION;
            b.va += b.vy * 0.04;
          } else if (b.x > leftWall + width - b.r) {
            b.x = leftWall + width - b.r;
            b.vx = -Math.abs(b.vx) * WALL_RESTITUTION;
            b.va -= b.vy * 0.04;
          }

          if (isFullPage && b.y < ceiling + b.r) {
            b.y = ceiling + b.r;
            b.vy = Math.abs(b.vy) * WALL_RESTITUTION;
          } else if (!isFullPage && b.y < b.r) {
            b.y = b.r;
            b.vy = Math.abs(b.vy) * WALL_RESTITUTION;
          } else if (isFullPage && b.y > floor - b.r) {
            b.y = floor - b.r;
            b.vy = -Math.abs(b.vy) * WALL_RESTITUTION;
            b.vx *= FLOOR_FRICTION;
            b.va = b.vx * 2.2;
          } else if (!isFullPage && b.y > height - b.r) {
            b.y = height - b.r;
            b.vy = -Math.abs(b.vy) * WALL_RESTITUTION;
            b.vx *= FLOOR_FRICTION;
            b.va = b.vx * 2.2;
          }

          b.a += b.va;
          b.va *= 0.985;
        }
      }

      for (let i = 0; i < bodies.length; i++) {
        for (let j = i + 1; j < bodies.length; j++) {
          collide(bodies[i], bodies[j]);
        }
      }

      for (const b of bodies) {
        b.x = clamp(b.x, leftWall + b.r, Math.max(leftWall + b.r, leftWall + width - b.r));
        b.y = clamp(b.y, isFullPage ? ceiling + b.r : b.r, isFullPage ? Math.max(ceiling + b.r, floor - b.r) : Math.max(b.r, height - b.r));
        b.el.style.transform = `translate(${(b.x - b.r).toFixed(2)}px, ${(b.y - b.r).toFixed(
          2
        )}px) rotate(${b.a.toFixed(2)}deg)`;
      }

      rafId = requestAnimationFrame(step);
    };

    const start = () => {
      if (running || document.hidden) return;
      running = true;
      measure();
      rafId = requestAnimationFrame(step);
    };
    const stop = () => {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }
      if (isFullPage || !intersectionObserver) {
        start();
        return;
      }
      const rect = arena.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < (window.innerHeight || 0)) start();
    };

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => measure());
      resizeObserver.observe(arena);
    } else {
      window.addEventListener('resize', measure, { passive: true });
    }

    document.addEventListener('visibilitychange', onVisibility);

    if (isFullPage) {
      start();

      if (tiltGravity) {
        const needsIosPermission =
          (typeof DeviceOrientationEvent !== 'undefined' &&
            typeof DeviceOrientationEvent.requestPermission === 'function') ||
          (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function');

        if (needsIosPermission) {
          tiltUnlock = document.createElement('button');
          tiltUnlock.type = 'button';
          tiltUnlock.className = 'beybling-arena__tilt-unlock';
          tiltUnlock.textContent = 'Tilt';
          tiltUnlock.setAttribute('aria-label', 'Enable tilt controls');
          tiltUnlock.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            tiltGravity.requestOnGesture();
          });
          document.body.appendChild(tiltUnlock);
        } else {
          tiltGravity.startListening();
        }
      }

      requestAnimationFrame(() => {
        measure();
        for (const b of bodies) {
          b.x = clamp(b.x, leftWall + b.r, Math.max(leftWall + b.r, leftWall + width - b.r));
          b.y = clamp(b.y, ceiling + b.r, Math.max(ceiling + b.r, floor - b.r));
        }
      });
    } else if ('IntersectionObserver' in window) {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) start();
            else stop();
          }
        },
        { threshold: 0.01 }
      );
      intersectionObserver.observe(arena);
    } else {
      start();
    }

    const cleanup = () => {
      stop();
      tiltUnlock?.remove();
      tiltUnlock = null;
      tiltGravity?.stopListening();
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', measure);
    };
    arenaCleanups.set(arena, cleanup);
    arena.dataset.beyblingArenaReady = 'true';
  }

  const boot = () => {
    document.querySelectorAll('.beybling-arena:not(.beybling-arena--duel)').forEach((arena) => {
      if (arena instanceof HTMLElement) initArena(arena);
    });
  };

  const restartArenas = () => {
    document.querySelectorAll('.beybling-arena:not(.beybling-arena--duel)').forEach((arena) => {
      if (arena instanceof HTMLElement) destroyArena(arena);
    });
    boot();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('pagehide', () => {
    document.querySelectorAll('.beybling-arena:not(.beybling-arena--duel)').forEach((arena) => {
      if (arena instanceof HTMLElement) destroyArena(arena);
    });
  });

  window.addEventListener('pageshow', (event) => {
    if (!document.body.classList.contains('beybling-contact-arena')) return;

    const arena = document.querySelector('.beybling-arena--fullpage:not(.beybling-arena--duel)');
    if (!(arena instanceof HTMLElement)) return;

    const hasLogos = Boolean(arena.querySelector('.beybling-arena__logo'));
    const needsRestart =
      event.persisted ||
      !hasLogos ||
      (arena.dataset.beyblingArenaReady === 'true' && !arenaCleanups.has(arena));

    if (needsRestart) restartArenas();
  });
})();
